import {
  ArrowsDragIcon,
  Button,
  ChartIcon,
  CopyIcon,
  EditIcon,
  FileAddAltIcon,
  Header,
  Paragraph,
  ResponsiveButton,
  SearchIcon,
  SendIcon,
  TextInput,
  ToggleInput,
  TrashCanIcon,
} from "@datagame/ui";
import { ChangeEvent, useMemo, useState } from "react";
import { sortBy } from "lodash";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";

import { Project, ProjectStatus } from "../../../../gql/graphql";
import { PROJECT_COPY, PROJECT_UPDATE } from "../../../../graphql/mutations/ProjectMutations";
import { PROJECT_LIST } from "../../../../graphql/queries/ProjectQueries";
import { TableConfig } from "../../../../types/table";
import { DeleteProjectModal } from "../../../shared/modals/DeleteProjectModal";
import { ManageFoldersModal } from "../../../shared/modals/ManageFoldersModal";
import { ShareProjectModal } from "../../../shared/modals/ShareProjectModal";
import { Dropdown } from "../../../shared/ui/Dropdown";
import { Table } from "../../../shared/ui/Table";
import { type Folder } from "../FoldersSection/FoldersList";
import { EmptyList } from "../../../shared/EmptyList";

import styles from "./ProjectsSection.module.scss";
import { DeleteProjectDataModal } from "../../../shared/modals/DeleteProjectDataModal";

const cx = classNames.bind(styles);

export interface ProjectsSectionProps {
  projects?: Project[];
  groupedFolders: Folder[];
  selectedFolder?: Folder;
}

export const ProjectsSection = ({ projects, selectedFolder, groupedFolders }: ProjectsSectionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [updateProject] = useMutation(PROJECT_UPDATE);
  const [copyProject] = useMutation(PROJECT_COPY, { refetchQueries: [{ query: PROJECT_LIST }] });

  const [searchTerm, setSearchTerm] = useState("");
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteDataModalOpen, setIsDeleteDataModalOpen] = useState(false);
  const [isManageFoldersModalOpen, setIsManageFoldersModalOpen] = useState(false);
  const [moveToFolder, setMoveToFolder] = useState<Folder>();

  const handleCopy = (projectId: string) => {
    copyProject({
      variables: {
        projectId,
      },
    });
  };

  const tableConfig = useMemo<TableConfig<Project>>(
    () => ({
      getRowClassName: () => styles.rowWrapper,
      columns: [
        {
          header: t("projects:name"),
          width: 300,
          render: ({ name }) => (
            <Paragraph size="small" weight="font-bold">
              {name}
            </Paragraph>
          ),
        },
        {
          header: t("projects:live"),
          width: 200,
          render: (item) => {
            const responseCount = item.games.map((game) => game.responseCount).sort()[0];
            return <Paragraph size="small">{responseCount ?? 0}</Paragraph>;
          },
        },
        {
          header: t("projects:status"),
          width: 200,
          render: (item) => {
            const options = [
              {
                icon: <SendIcon />,
                name: t("projects:actions.share"),
                onClick: () => {
                  setIsShareModalOpen(true);
                  setSelectedProject(item);
                },
              },
              {
                icon: <EditIcon />,
                name: t("projects:actions.edit"),
                onClick: () => {
                  navigate(`project?id=${item.id}`);
                },
              },
              {
                onClick: () => {
                  navigate(`reports/${item.id}`);
                },
                icon: <ChartIcon className={styles.reportIcon} />,
                name: t("projects:actions.report"),
              },
              {
                onClick: () => {
                  handleCopy(item.id);
                },
                icon: <FileAddAltIcon />,
                name: t("projects:actions.copy"),
              },
              {
                onClick: () => {
                  setSelectedProject(item);
                  setIsManageFoldersModalOpen(true);
                },
                icon: <ArrowsDragIcon />,
                name: t("projects:actions.move"),
              },
              {
                onClick: () => {
                  setSelectedProject(item);
                  setIsDeleteModalOpen(true);
                },
                icon: <TrashCanIcon className={styles.deleteIcon} />,
                name: t("projects:actions.delete"),
                className: styles.deleteText,
              },
            ];

            const isLive = item.status === ProjectStatus.Live;

            const handleChangeStatus = () => {
              updateProject({
                variables: {
                  projectId: item.id,
                  data: {
                    directoryId: item.directory.id,
                    status: isLive ? ProjectStatus.Closed : ProjectStatus.Live,
                  },
                },
              });
            };

            return (
              <div className={styles.actionsWrapper}>
                {/* eslint-disable-next-line */}
                <ToggleInput name="status" value={isLive} onChange={handleChangeStatus} />

                <div className={cx("statusActions")}>
                  <Dropdown options={options}></Dropdown>
                </div>
              </div>
            );
          },
        },
        {
          header: t("projects:actions.title"),
          className: styles.actionsCell,
          width: 200,
          render: (item) => {
            const options = [
              {
                icon: <EditIcon />,
                name: t("projects:actions.edit"),
                onClick: () => {
                  navigate(`project?id=${item.id}`);
                },
              },
              {
                onClick: () => {
                  navigate(`reports/${item.id}`);
                },
                icon: <ChartIcon className={styles.reportIcon} />,
                name: t("projects:actions.report"),
              },
              {
                onClick: () => {
                  handleCopy(item.id);
                },
                icon: <CopyIcon />,
                name: t("projects:actions.copy"),
              },
              {
                onClick: () => {
                  setSelectedProject(item);
                  setIsManageFoldersModalOpen(true);
                },
                icon: <ArrowsDragIcon />,
                name: t("projects:actions.move"),
              },
              {
                onClick: () => {
                  setSelectedProject(item);
                  setIsDeleteDataModalOpen(true);
                },
                icon: <TrashCanIcon className={styles.deleteIcon} />,
                name: t("projects:actions.deleteData"),
                className: styles.deleteText,
              },
              {
                onClick: () => {
                  setSelectedProject(item);
                  setIsDeleteModalOpen(true);
                },
                icon: <TrashCanIcon className={styles.deleteIcon} />,
                name: t("projects:actions.delete"),
                className: styles.deleteText,
              },
            ];

            const handleShareClick = () => {
              setSelectedProject(item);
              setIsShareModalOpen(true);
            };
            return (
              <div className={styles.actionsWrapper}>
                <Button
                  size="small"
                  icon={<SendIcon />}
                  iconSide="left"
                  kind="minimal"
                  className={styles.shareButton}
                  onClick={handleShareClick}
                >
                  {t("projects:actions.share")}
                </Button>
                <div className={styles.contextMenuWrapper}>
                  <Dropdown options={options}></Dropdown>
                </div>
              </div>
            );
          },
        },
      ],
    }),
    [],
  );

  const sortedProject = useMemo(() => {
    return sortBy(projects, "createdAt");
  }, [projects]);

  const filteredProjects = useMemo(() => {
    if (!projects || !selectedFolder) {
      return [];
    }
    return sortedProject.filter((project) => project.directory.id === selectedFolder.id);
  }, [sortedProject, selectedFolder?.id]);

  const filteredBySearchText = useMemo(() => {
    return filteredProjects.filter((project) => project.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [filteredProjects, searchTerm]);

  const renderTable = () => {
    if (filteredProjects.length === 0) {
      return <EmptyList title={t("projects:emptyTitle")} description={t("projects:emptyDescription")} />;
    }
    return <Table data={filteredBySearchText as Project[]} config={tableConfig} />;
  };

  const renderHeader = () => {
    if (selectedFolder?.parentDirectory) {
      return (
        <>
          <span className={styles.parentHeader}>{selectedFolder.parentDirectory.name} /</span> {selectedFolder.name}
        </>
      );
    }
    return <span>{selectedFolder?.name}</span>;
  };

  const handleCreateProject = () => {
    navigate("/project", { state: { folder: selectedFolder } });
  };

  // TODO: use debounce
  const changeSearchTerm = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
    setSelectedProject(undefined);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedProject(undefined);
  };

  const closeDeleteDataModal = () => {
    setIsDeleteDataModalOpen(false);
    setSelectedProject(undefined);
  };

  const handleMoveToFolderChange = (folder: Folder) => {
    setMoveToFolder(folder);
  };

  const handleMove = (newDirectoryId?: string) => {
    selectedProject &&
      newDirectoryId &&
      updateProject({
        variables: {
          projectId: selectedProject.id,
          data: {
            directoryId: newDirectoryId,
          },
        },
        refetchQueries: [{ query: PROJECT_LIST }],
      });
    setIsManageFoldersModalOpen(false);
    setSelectedProject(undefined);
  };

  return (
    <div className={cx("itemContainer", "projectContainer")}>
      <div className={styles.projectHeaderWrapper}>
        <div className={styles.projectHeader}>
          <Header className={styles.header} variant="h6">
            {renderHeader()}
          </Header>
          <Paragraph size="small" color="secondary">
            {t("projects:sectionDescription")}
          </Paragraph>
        </div>
        <ResponsiveButton
          size="medium"
          className={styles.projectButton}
          iconSide="left"
          icon={<FileAddAltIcon />}
          onClick={handleCreateProject}
        >
          {t("projects:createProject")}
        </ResponsiveButton>
      </div>
      <TextInput
        type="text"
        value={searchTerm}
        onChange={changeSearchTerm}
        placeholder={t("projects:searchPlaceholder") ?? ""}
        size="small"
        icon={<SearchIcon />}
        className={styles.searchInput}
      />

      {renderTable()}

      {isShareModalOpen && <ShareProjectModal project={selectedProject} onClose={closeShareModal} />}

      {isDeleteModalOpen && <DeleteProjectModal onClose={closeDeleteModal} project={selectedProject} />}

      {isDeleteDataModalOpen && <DeleteProjectDataModal onClose={closeDeleteDataModal} project={selectedProject} />}

      {isManageFoldersModalOpen && (
        <ManageFoldersModal
          onClose={() => setIsManageFoldersModalOpen(false)}
          folders={groupedFolders}
          selectedFolder={moveToFolder}
          setSelectedFolder={handleMoveToFolderChange}
          canManage={false}
          title={t("folders:selectFolder") ?? ""}
          description={t("folders:selectDescription") ?? ""}
          confirmText={t("folders:move") ?? ""}
          confirmAction={handleMove}
        />
      )}
    </div>
  );
};
